// PARTNER-SIDEBAR — üveg-panel (240px), a terv szerkezetével: márka · fiók-váltó · nav · segítség-kártya ·
// felhasználó-kártya · jogi linkek.
//
// A KERET a belső felülettel AZONOS geometriájú (`sidebar-glass m-2 rounded-2xl w-60`) — a design-mérés szerint
// a két felület ugyanaz a rendszer, csak más az információ-architektúra (`docs/foundations/design.md` §9).
// Ami MÁS: nincs összecsukás (a partnernek négy menüpontja van, nincs mit spórolni), nincs modul-csoportosítás,
// és van fiók-váltó + segítség-kártya.
import type { PortalMeResponse } from '@atlas/contracts';
import { PORTAL_PERMISSIONS } from '@atlas/shared';
import {
  BuildingLibraryIcon,
  BuildingOffice2Icon,
  CheckIcon,
  ChevronRightIcon,
  ChevronUpDownIcon,
  PlusIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AtlasMark } from '@/components/brand/atlas-logo';
import { visiblePortalNav } from '@/components/portal/portal-nav';
import { Avatar } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Text } from '@/components/ui/text';
import { portalPermissionsOf } from '@/features/portal/use-portal-permissions';
import { cn } from '@/lib/utils';

export interface PortalSidebarProps {
  me: PortalMeResponse | undefined;
  /** A FIÓK-SCOPE: a kiválasztott fiók, VAGY `null` = „Minden fiók" (aggregált nézet — ez az alapérték). */
  accountId: string | null;
  onAccountChange: (accountId: string | null) => void;
  /** Mobil menüben laposan renderel (nincs üveg-panel, nincs margó). */
  flat?: boolean;
  onNavigate?: () => void;
}

/** A fiók-váltó tartalma — a mobil menü is ezt használja (nincs második implementáció). */
export function PortalAccountList({
  me,
  accountId,
  onAccountChange,
}: {
  me: PortalMeResponse | undefined;
  accountId: string | null;
  onAccountChange: (id: string | null) => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div>
      <Text variant="overline" as="div" className="px-2 pb-1 pt-1.5">
        {t('portal.shell.myAccounts')}
      </Text>
      {/* „MINDEN FIÓK" — a scope ALAPÉRTELMEZETT állapota, és tudatosan a lista ELEJÉN áll.
          MIÉRT LÉTEZIK: a partner leggyakoribb kérdései kereszt-fiókosak („melyik számlám lejárt?",
          „hol vár rám válasz?"), és a nav-jelvények is összegzők. Enélkül egy 30 propertys csoportnál
          30 váltás kellene egy kérdéshez, a jelvények pedig csendben egy fiók számát mutatnák. */}
      <button
        type="button"
        onClick={() => onAccountChange(null)}
        className="hover:bg-accent flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors"
      >
        <BuildingLibraryIcon className="text-muted-foreground size-4 shrink-0" />
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-sm">{t('portal.shell.allAccounts')}</span>
          <Text variant="secondary" as="span" className="truncate text-[11px]">
            {t('portal.shell.allAccountsHint', { count: me?.accounts.length ?? 0 })}
          </Text>
        </span>
        {accountId === null && <CheckIcon className="text-brand size-4 shrink-0" />}
      </button>
      {/* GÖRGETHETŐ lista: egy partner-kapcsolattartó akárhány fiókhoz tartozhat (mérve: 20+), és a
          popover fix magasságában a lista alja korábban ELÉRHETETLEN volt. A `max-h-64` a repo
          konvenciója a legördülő listákra (`entity-combobox`, `entity-multiselect`). */}
      <div className="max-h-64 space-y-0.5 overflow-y-auto">
        {(me?.accounts ?? []).map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => onAccountChange(a.id)}
            className="hover:bg-accent flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors"
          >
            <BuildingOffice2Icon className="text-muted-foreground size-4 shrink-0" />
            <span className="flex min-w-0 flex-col">
              <span className="truncate text-sm">{a.name}</span>
              <Text variant="secondary" as="span" className="truncate text-[11px]">
                {/* Az ELSŐDLEGES-tény itt is látszik: ez magyarázza, miért lát a partner az egyik fiókon
                  mindent, a másikon csak a sajátját. */}
                {a.clientName ?? '—'}
                {a.isPrimary ? ` · ${t('portal.shell.primaryContact')}` : ''}
              </Text>
            </span>
            {accountId === a.id && <CheckIcon className="text-brand size-4 shrink-0" />}
          </button>
        ))}
      </div>
    </div>
  );
}

export function PortalSidebar({
  me,
  accountId,
  onAccountChange,
  flat = false,
  onNavigate,
}: PortalSidebarProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [switcherOpen, setSwitcherOpen] = useState(false);
  // A `null` scope („Minden fiók") NEM esik vissza az első fiókra: a trigger is ezt írja ki, különben a
  // felhasználó azt hinné, hogy egy fiókra szűkített nézetet lát.
  // A jegy-nyitás CTA-ja csak annak, aki VALAMELY fiókon nyithat jegyet (`ticketsWrite`).
  const canOpenTicket = portalPermissionsOf(me).has(PORTAL_PERMISSIONS.ticketsWrite);
  const current = accountId === null ? undefined : me?.accounts.find((a) => a.id === accountId);

  return (
    <aside
      className={cn(
        'flex flex-col',
        flat ? 'h-full' : 'sidebar-glass m-2 hidden w-60 shrink-0 rounded-2xl md:flex',
      )}
    >
      {/* MÁRKA — a „Partner" jelölés mondja meg, melyik felületen vagyunk (a belső shellben app-váltó van). */}
      <div className="flex items-center gap-2.5 px-3.5 pb-3 pt-3.5">
        <AtlasMark size={26} />
        <Text variant="title" as="span" className="tracking-[-0.015em]">
          Atlas
        </Text>
        <span className="bg-brand-soft text-brand-dark dark:text-brand rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em]">
          {t('portal.brandTag')}
        </span>
      </div>

      {/* FIÓK-VÁLTÓ — a felület EGYETLEN scope-vezérlője: minden lista, a kezdőlap, a nav-jelvények és a
          jegy-nyitó is ezen szűr. CSAK EGYNÉL TÖBB FIÓK esetén jelenik meg: a portál-felhasználók
          79%-ának (mérve az éles adaton) pontosan egy fiókja van, nekik a váltó tiszta zaj — és a
          „Minden fiók" ugyanazt az egy fiókot adná, mint a kiválasztása. */}
      {(me?.accounts.length ?? 0) > 1 && (
        <Popover open={switcherOpen} onOpenChange={setSwitcherOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={t('portal.shell.switchAccount')}
              className="border-border bg-card hover:border-brand hover:bg-accent ease-atlas mx-2.5 mb-2.5 flex items-center gap-2.5 rounded-[10px] border px-2.5 py-2 text-left transition-colors duration-150"
            >
              <span className="bg-brand-soft text-brand flex size-[26px] shrink-0 items-center justify-center rounded-lg">
                <BuildingOffice2Icon className="size-[15px]" />
              </span>
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm font-medium leading-tight">
                  {current?.name ?? t('portal.shell.allAccounts')}
                </span>
                <Text variant="secondary" as="span" className="truncate text-[11px]">
                  {current?.clientName ??
                    t('portal.shell.allAccountsHint', { count: me?.accounts.length ?? 0 })}
                </Text>
              </span>
              <ChevronUpDownIcon className="text-muted-foreground size-3.5 shrink-0" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[252px] p-1.5">
            <PortalAccountList
              me={me}
              accountId={accountId}
              onAccountChange={(id) => {
                onAccountChange(id);
                setSwitcherOpen(false);
              }}
            />
          </PopoverContent>
        </Popover>
      )}

      <nav
        aria-label={t('portal.nav.aria')}
        className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-2.5 pb-1 pt-1.5"
      >
        {visiblePortalNav(me).map(({ to, labelKey, Icon, badge }) => {
          // A kezdőlap CSAK pontos egyezésre aktív (különben minden aloldalon az is aktívnak látszana).
          const active = to === '/portal' ? pathname === '/portal' : pathname.startsWith(to);
          const count = me && badge ? badge(me.counts) : null;
          return (
            <Link
              key={to}
              to={to}
              onClick={onNavigate}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'ease-atlas flex items-center gap-2.5 rounded-[9px] px-2.5 py-2 text-sm transition-colors duration-150',
                active
                  ? 'bg-brand-soft text-foreground font-semibold'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground font-normal',
              )}
            >
              <Icon className={cn('size-[17px] shrink-0', active && 'text-brand')} />
              <span className="min-w-0 flex-1 truncate">{t(labelKey)}</span>
              {count !== null && (
                <span className="bg-brand inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1.5 text-[10.5px] font-semibold leading-none text-white">
                  {count}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* SEGÍTSÉG-KÁRTYA — a partner-felület fő belépője a jegy-nyitáshoz (a terv `help-card`-ja). */}
      {canOpenTicket && (
        <div className="px-2.5 pt-2.5">
          <div className="border-border from-brand-soft rounded-xl border bg-gradient-to-br to-transparent p-3">
            <div className="flex items-center gap-2">
              <SparklesIcon className="text-brand size-[15px]" />
              <Text variant="itemTitle" as="span">
                {t('portal.shell.helpTitle')}
              </Text>
            </div>
            <Text variant="secondary" as="p" className="mb-2.5 mt-1.5 text-[11.5px] leading-snug">
              {t('portal.shell.helpBody')}
            </Text>
            <Button
              variant="brand"
              size="sm"
              className="w-full"
              onClick={() => {
                onNavigate?.();
                void navigate({ to: '/portal/tickets/new' });
              }}
            >
              <PlusIcon />
              {t('portal.newTicket.action')}
            </Button>
          </div>
        </div>
      )}

      {/* FELHASZNÁLÓ-KÁRTYA = a profil belépője (a terv szerint a profil a láblécben van, nem a navban).
          JOGI LAPOK ITT NINCSENEK (user-döntés 2026-09-02): a Profil lap „Jogi tudnivalók" szekciója viszi
          őket, és egy ritkán használt link a keretben csak zaj — a navban négy dolog van, nem hat. */}
      <div className="flex flex-col gap-1 px-2.5 pb-2.5 pt-1.5">
        <Link
          to="/portal/profile"
          onClick={onNavigate}
          aria-current={pathname.startsWith('/portal/profile') ? 'page' : undefined}
          className={cn(
            'ease-atlas flex items-center gap-2.5 rounded-[10px] px-2 py-2 transition-colors duration-150',
            pathname.startsWith('/portal/profile') ? 'bg-brand-soft' : 'hover:bg-accent',
          )}
        >
          <Avatar name={me?.identity.name ?? ''} src={me?.identity.avatarUrl ?? null} size="md" />
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-[12.5px] font-semibold leading-tight">
              {me?.identity.name ?? '—'}
            </span>
            <Text variant="secondary" as="span" className="truncate text-[11px] leading-tight">
              {me?.identity.email ?? ''}
            </Text>
          </span>
          <ChevronRightIcon className="text-muted-foreground size-3.5 shrink-0" />
        </Link>
      </div>
    </aside>
  );
}
